import { applyContentCleanup } from './contentCleanup.js';
import { createBlockStash } from './codeBlocks.js';

// Format results from API call (keeps your markdown-ish logic, slightly cleaned)
export function formatResponse(responseData) {
  let content = responseData.content || 'No content available. Please check your API configuration.';

  console.log('📝 Formatting response, content length:', content.length);
  console.log('📝 Raw content preview:', content.substring(0, 500));
  console.log('📝 Raw content FULL:', content);

  // Log table-like content for debugging
  const tableRows = content.match(/\|.+\|/gm);
  if (tableRows) {
    console.log('📊 Found', tableRows.length, 'lines containing pipes in raw content');
    console.log('📊 First table row sample:', tableRows[0]);
    console.log('📊 All table rows:', tableRows);
  } else {
    console.log('⚠️ No table rows found in raw content');
  }

  // Remove Grok/Perplexity meta tags before processing
  content = content.replace(/<grok:render[^>]*>[\s\S]*?<\/grok:render>/g, '');
  content = content.replace(/<argument[^>]*>[\s\S]*?<\/argument>/g, '');

  // Preserve network-slice-wiz web component before HTML escaping
  const webComponents = [];
  content = content.replace(/(<network-slice-wiz[^>]*>[\s\S]*?<\/network-slice-wiz>)/g, (match) => {
    const placeholder = `__WEB_COMPONENT_${webComponents.length}__`;
    webComponents.push(match);
    return placeholder;
  });

  // Escape HTML first to prevent XSS
  content = content
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Code blocks (triple and double backticks). Arrow chains become flow
  // diagrams, everything else a wrapping <pre>. The rendered HTML is parked
  // behind a token so none of the rules below can reach into it — they would
  // otherwise flatten its newlines and eat characters like "+" (codeBlocks.js).
  const blockStash = createBlockStash();
  content = blockStash.stash(content);

  // Tables - more robust regex to capture complete tables
  // First, handle traditional markdown tables with separator lines
  content = content.replace(
    /^\s*(\|.+\|)\s*\n\s*(\|[\s\-:|]+\|)\s*\n((?:\s*\|.+\|(?:\s*\n|$))*)/gm,
    function (match, headerLine, separatorLine, rowsSection) {
      console.log('📊 Found markdown table with separator');
      console.log('📊 Header line:', headerLine);
      console.log('📊 Row section:', rowsSection);

      // Extract header - remove leading/trailing pipes and trim
      const header = headerLine.replace(/^\s*\||\|\s*$/g, '').trim();

      let html = '<table class="api-table"><thead><tr>';
      header
        .split('|')
        .forEach(cell => {
          const trimmedCell = cell.trim();
          if (trimmedCell) {
            html += `<th>${trimmedCell}</th>`;
          }
        });
      html += '</tr></thead><tbody>';

      // Process rows
      const rows = rowsSection.trim().split('\n');
      rows.forEach(row => {
        if (!row.trim() || !row.includes('|')) return;
        const cleanRow = row.replace(/^\s*\||\|\s*$/g, '').trim();
        if (!cleanRow) return;

        html += '<tr>';
        cleanRow.split('|').forEach(cell => {
          const trimmedCell = cell.trim();
          if (trimmedCell) {
            html += `<td>${trimmedCell}</td>`;
          }
        });
        html += '</tr>';
      });

      html += '</tbody></table>';
      console.log('📊 Generated table HTML:', html);
      console.log('📊 Table HTML length:', html.length);
      return html + '\n'; // Preserve line break for subsequent content
    }
  );

  // Group consecutive pipe-delimited rows into tables (without separator lines)
  content = content.replace(
    /((?:^\s*\|.+\|\s*$\n?)+)/gm,
    function(match) {
      // Skip if already processed as table
      if (match.includes('<table') || match.includes('<th>')) return match;

      const lines = match.trim().split('\n').filter(line => line.trim());
      if (lines.length < 2) return match; // Need at least 2 rows for a table

      console.log('📊 Grouping', lines.length, 'consecutive rows into table');

      // First row is header
      const headerCells = lines[0].replace(/^\s*\||\|\s*$/g, '').split('|').map(c => c.trim()).filter(c => c);

      let html = '<table class="api-table"><thead><tr>';
      headerCells.forEach(cell => {
        html += `<th>${cell}</th>`;
      });
      html += '</tr></thead><tbody>';

      // Rest are data rows
      for (let i = 1; i < lines.length; i++) {
        const cells = lines[i].replace(/^\s*\||\|\s*$/g, '').split('|').map(c => c.trim()).filter(c => c);
        html += '<tr>';
        cells.forEach(cell => {
          html += `<td>${cell}</td>`;
        });
        html += '</tr>';
      }

      html += '</tbody></table>\n';
      return html;
    }
  );

  // Check if tables were successfully converted
  const tableCount = (content.match(/<table/g) || []).length;
  console.log('📊 After table processing, found', tableCount, 'table tags in content');
  if (tableCount > 0) {
    const tablePreview = content.match(/<table[\s\S]{0,200}/);
    console.log('📊 Table preview:', tablePreview ? tablePreview[0] : 'Not found');
  }

  // Convert structured bullet lists to tables (e.g., API responses with key-value pairs)
  // Pattern: - **Key** – Description (with various separators: en-dash, em-dash, colon, hyphen)
  // Only applies to TOP-LEVEL bullets (no indentation) to avoid conflicts with nested lists
  content = content.replace(
    /(?:^- \*\*[^*]+\*\*\s*[–—:\-]\s*.+$(?:\n|$))+/gm,
    function(match, offset, fullString) {
      const lines = match.trim().split('\n').filter(line => line.trim());

      // Need at least 2 lines to make a table worthwhile
      if (lines.length < 2) return match;

      // Check if this block is followed by indented bullets (nested content)
      // If so, don't convert to table - it's a nested list structure
      const afterMatch = fullString.substring(offset + match.length);
      const nextLine = afterMatch.match(/^[ \t]*[^\n]*/);
      if (nextLine && /^  +[-*+]/.test(nextLine[0])) {
        // Next line is an indented bullet - this is a nested list, not a table
        return match;
      }

      console.log('📊 Converting structured bullet list to table, found', lines.length, 'items');

      // Check if all lines follow the pattern AND have substantial content after separator
      const validLines = lines.filter(line => {
        const match = line.match(/^- \*\*[^*]+\*\*\s*[–—:\-]\s*(.+)$/);
        if (!match) return false;
        // Require substantial content (not just whitespace or very short text)
        const content = match[1].trim();
        return content.length > 3;
      });

      if (validLines.length < 2) return match;

      let html = '<table class="api-table"><thead><tr><th>Item</th><th>Description</th></tr></thead><tbody>';

      validLines.forEach(line => {
        const itemMatch = line.match(/^- \*\*([^*]+)\*\*\s*[–—:\-]\s*(.+)$/);
        if (itemMatch) {
          const key = itemMatch[1].trim();
          const description = itemMatch[2].trim();
          html += `<tr><td><strong>${key}</strong></td><td>${description}</td></tr>`;
        }
      });

      html += '</tbody></table>\n';
      console.log('📊 Generated table from bullet list, HTML length:', html.length);
      return html;
    }
  );

  // Inline code
  content = content.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Headers (allow optional leading whitespace)
  const beforeHeaders = content;
  content = content.replace(/^\s*###### (.*)$/gm, '<h6>$1</h6>');
  content = content.replace(/^\s*##### (.*)$/gm, '<h5>$1</h5>');
  content = content.replace(/^\s*#### (.*)$/gm, '<h4>$1</h4>');
  content = content.replace(/^\s*### (.*)$/gm, '<h3>$1</h3>');
  content = content.replace(/^\s*## (.*)$/gm, '<h2>$1</h2>');
  content = content.replace(/^\s*# (.*)$/gm, '<h1>$1</h1>');
  if (beforeHeaders !== content) {
    console.log('✅ Header conversion applied. Sample:', content.substring(0, 500));
  } else {
    console.log('⚠️  No headers found to convert');
  }

  // Bold and italic
  content = content.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
  content = content.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  content = content.replace(/\*(.+?)\*/g, '<strong>$1</strong>');
  content = content.replace(/\*([^*]+)\*/g, '<em>$1</em>');

  content = content.replace(/___(.+?)___/g, '<strong><em>$1</em></strong>');
  content = content.replace(/__(.+?)__/g, '<strong>$1</strong>');
  content = content.replace(/_(.+?)_/g, '<em>$1</em>');

  // Links
  content = content.replace(/\[([^\]]+)\]\(([^)]+)\)/g, function (match, text, url) {
    const unescapedUrl = url
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
    return `<a href="${unescapedUrl}" target="_blank" rel="noopener noreferrer">${text}</a>`;
  });

  // Ordered lists (handle optional leading whitespace)
  content = content.replace(/^\s*(\d+)\. (.+)$/gm, '<li-ol>$2</li-ol>');
  content = content.replace(/(<li-ol>[^<]*<\/li-ol>(?:\s*<li-ol>[^<]*<\/li-ol>)*)/gs, function (match) {
    // Remove all whitespace between list items for clean formatting
    return '<ol>' + match.replace(/\s+/g, ' ').replace(/\s*<\/li-ol>\s*<li-ol>\s*/g, '</li><li>').replace(/<li-ol>/g, '<li>').replace(/<\/li-ol>/g, '</li>') + '</ol>';
  });
  // Clean up any remaining li-ol tags
  content = content.replace(/<li-ol>/g, '<li>').replace(/<\/li-ol>/g, '</li>');

  // Block bullets - handle multi-level nested lists by preserving indentation
  // Mark bullets with their indentation level (0, 2, 4, 6 spaces, etc.)
  // Supports -, *, +, and • (Unicode bullet U+2022)
  content = content.replace(/^([ ]*)[\*\-\+•] (.+)$/gm, function(match, spaces, text) {
    const level = spaces.length / 2; // 0 spaces = level 0, 2 spaces = level 1, 4 = level 2, etc.
    // Trim trailing spaces to prevent unwanted <br> tags from markdown line breaks
    return `<li-ul-L${level}>${text.trim()}</li-ul-L${level}>`;
  });

  // Process from deepest nesting to shallowest to build proper hierarchy
  // Level 3 (6 spaces)
  content = content.replace(/((?:<li-ul-L3>.*?<\/li-ul-L3>\s*)+)/g, function(match) {
    return '<ul class="nested-list level-3">' + match.replace(/\s*<\/li-ul-L3>\s*<li-ul-L3>\s*/g, '</li><li>').replace(/<li-ul-L3>/g, '<li>').replace(/<\/li-ul-L3>/g, '</li>') + '</ul>';
  });
  // Move level-3 nested lists inside their parent level-2 items
  content = content.replace(/<\/li-ul-L2>\s*(<ul class="nested-list level-3">.*?<\/ul>)/gs, ' $1</li-ul-L2>');

  // Level 2 (4 spaces)
  content = content.replace(/((?:<li-ul-L2>(?:.*?(?:<ul class="nested-list level-3">.*?<\/ul>)?.*?)<\/li-ul-L2>\s*)+)/gs, function(match) {
    return '<ul class="nested-list level-2">' + match.replace(/\s*<\/li-ul-L2>\s*<li-ul-L2>\s*/g, '</li><li>').replace(/<li-ul-L2>/g, '<li>').replace(/<\/li-ul-L2>/g, '</li>') + '</ul>';
  });
  // Move level-2 nested lists inside their parent level-1 items
  content = content.replace(/<\/li-ul-L1>\s*(<ul class="nested-list level-2">.*?<\/ul>)/gs, ' $1</li-ul-L1>');

  // Level 1 (2 spaces)
  content = content.replace(/((?:<li-ul-L1>(?:.*?(?:<ul class="nested-list level-[23]">.*?<\/ul>)?.*?)<\/li-ul-L1>\s*)+)/gs, function(match) {
    return '<ul class="nested-list level-1">' + match.replace(/\s*<\/li-ul-L1>\s*<li-ul-L1>\s*/g, '</li><li>').replace(/<li-ul-L1>/g, '<li>').replace(/<\/li-ul-L1>/g, '</li>') + '</ul>';
  });
  // Move level-1 nested lists inside their parent level-0 items
  content = content.replace(/<\/li-ul-L0>\s*(<ul class="nested-list level-1">.*?<\/ul>)/gs, ' $1</li-ul-L0>');

  // Level 0 (top-level, no indentation)
  content = content.replace(/((?:<li-ul-L0>(?:.*?(?:<ul class="nested-list level-[123]">.*?<\/ul>)?.*?)<\/li-ul-L0>\s*)+)/gs, function(match) {
    return '<ul>' + match.replace(/\s*<\/li-ul-L0>\s*<li-ul-L0>\s*/g, '</li><li>').replace(/<li-ul-L0>/g, '<li>').replace(/<\/li-ul-L0>/g, '</li>') + '</ul>';
  });

  // Clean up any remaining tags
  for (let i = 0; i <= 3; i++) {
    content = content.replace(new RegExp(`<li-ul-L${i}>`, 'g'), '<li>');
    content = content.replace(new RegExp(`</li-ul-L${i}>`, 'g'), '</li>');
  }

  // Clean up excessive whitespace around lists to prevent unwanted line breaks
  // Remove multiple newlines after closing nested lists
  content = content.replace(/(<\/ul>)\s*\n\s*\n+\s*/g, '$1\n');
  // Normalize whitespace between closing and opening list tags
  content = content.replace(/(<\/li>)\s*\n\s*\n+\s*(<li>)/g, '$1\n$2');
  // Remove extra whitespace after list closings before next content
  content = content.replace(/(<\/ul>)\s+(<li>)/g, '$1 $2');

  // Inline bullets
  content = content.replace(/([^\n])[\*\-\+] ([^*\n]+)/g, function (match, prefix, item) {
    return prefix + '<span class="inline-bullet">• ' + item.trim() + '</span><br>';
  });

  // Apply content-specific cleanup and transformations
  const citations = responseData.citations || [];
  content = applyContentCleanup(content, citations);

  // Paragraphs - normalize newlines to avoid superfluous blank lines
  // First, replace triple+ newlines with double newlines
  content = content.replace(/\n{3,}/g, '\n\n');
  // Convert double newlines to paragraph breaks
  content = content.replace(/\n\n/g, '</p><p>');
  // Convert remaining single newlines to spaces (joining text within paragraphs)
  content = content.replace(/\n/g, ' ');
  // Clean up stray bullet characters - comprehensive removal
  // Handle multiple Unicode bullet variants: •, ·, ∙, ⋅, ◦, ▪, ▫, ‣, ⁃, ◘, ◙, and more
  // Pattern 1: Bullets with spaces around them
  content = content.replace(/\s+[•·∙⋅◦▪▫‣⁃◘◙∗⁎]\s+/g, ' ');
  // Pattern 2: Bullets attached to words
  content = content.replace(/([^\s>])[•·∙⋅◦▪▫‣⁃◘◙∗⁎]\s*/g, '$1 ');
  // Pattern 3: Any remaining standalone bullets (catch-all)
  content = content.replace(/[•·∙⋅◦▪▫‣⁃◘◙∗⁎]/g, ' ');
  // Clean up multiple spaces
  content = content.replace(/\s{2,}/g, ' ');
  // Wrap in paragraph tags
  content = '<p>' + content + '</p>';

  // Cleanup - remove empty paragraphs and fix nesting
  content = content.replace(/<p>\s*<\/p>/g, '');
  content = content.replace(/<p>(<[uo]l>)/g, '$1');
  content = content.replace(/(<\/[uo]l>)<\/p>/g, '$1');
  content = content.replace(/<p>(<table)/g, '$1');
  content = content.replace(/(<\/table>)<\/p>/g, '$1');
  content = content.replace(/<p>(<h[1-6]>)/g, '$1');
  content = content.replace(/(<\/h[1-6]>)<\/p>/g, '$1');
  // Remove paragraphs that only contain whitespace or breaks
  content = content.replace(/<p>\s*<\/p>/g, '');
  content = content.replace(/<p>(<br\s*\/?>)+\s*<\/p>/gi, '');
  content = content.replace(/<p>\s+<\/p>/g, '');
  // Remove empty paragraphs between structural elements
  content = content.replace(/(<\/h[1-6]>)\s*<p>\s*<\/p>\s*/gi, '$1');
  content = content.replace(/(<\/h[1-6]>)\s*<p>(<br\s*\/?>)+<\/p>\s*/gi, '$1');
  content = content.replace(/(<\/[uo]l>)\s*<p>\s*<\/p>\s*/gi, '$1');
  // Remove breaks immediately after list endings
  content = content.replace(/(<\/[uo]l>)\s*(<br\s*\/?>)+/gi, '$1');
  // Remove breaks immediately after headers
  content = content.replace(/(<\/h[1-6]>)\s*(<br\s*\/?>)+/gi, '$1');
  // Remove multiple consecutive breaks
  content = content.replace(/(<br\s*\/?>){2,}/gi, '<br>');
  // Final cleanup: remove any remaining empty paragraphs
  content = content.replace(/<p>\s*<\/p>/g, '');

  // Convert escaped <br> tags back to actual line breaks
  // This allows legitimate line breaks in content (especially in tables) to render properly
  content = content.replace(/&lt;br&gt;/gi, '<br>');
  content = content.replace(/&lt;br\s*\/&gt;/gi, '<br>');

  // Restore rendered code blocks / flow diagrams
  content = blockStash.restore(content);

  // Restore network-slice-wiz web components
  webComponents.forEach((component, index) => {
    content = content.replace(`__WEB_COMPONENT_${index}__`, component);
  });

  const relatedQuestions = responseData.related_questions || [];

  return { content, relatedQuestions, citations };
}
